import React, { useState, useEffect, useContext } from 'react';
import { Panel } from 'primereact/panel';
import { FormattedMessage } from 'react-intl';
import { Formik, Form } from 'formik';
import { Switch, Route, useRouteMatch } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import { growlErrors } from '../../utils/NotificationUtil';
import { Loading } from '../../components/Loading';
import AddDimension from './AddDimension';
import AddContext from './AddContext';
import AddBehaviorItems from './AddBehaviorItems';
import AddCognitiveItems from './AddCognitiveItems';
import AddReportTexts from './AddReportTexts';
import { ADD_ITEMBANK_CONTENT_CONFIG_QUERY } from './services/item-bank-management.service';
import {
  buildInitialValues,
  buildValidationSchema,
  getNextReportTextValues,
  INITIAL_CONFIG,
} from './helpers/item-bank-management.helper';
import useItembank from './hooks/useItembank';
import { AccountContext } from '../../components/AccountContext';
import { NotificationContext } from '../../components/GrowlNotifications';

const AddItemBankAssessmentContent = () => {
  const [config, setConfig] = useState(INITIAL_CONFIG);

  const { path } = useRouteMatch();
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const { handleSubmittingForm } = useItembank();

  const { loading, error, data } = useQuery(ADD_ITEMBANK_CONTENT_CONFIG_QUERY, { variables: { id: currentAccount } });

  const { addItemBankContentConfig } = data;

  useEffect(() => {
    if (addItemBankContentConfig) {
      setConfig((previousValue) => ({
        ...addItemBankContentConfig,
        reportTextValues: previousValue.reportTextValues,
      }));
    }
  }, [addItemBankContentConfig]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    growlErrors(growl, error);
  }

  const processFormValues = (values) => {
    const nextReportTextValues = getNextReportTextValues(values, config);

    setConfig({
      ...config,
      reportTextValues: nextReportTextValues,
    });
  };

  const initialValues = buildInitialValues(config);
  const validationSchema = buildValidationSchema();

  return (
    <div className="brand-detailed-wrapper">
      <div className="wrapper p-grid">
        <div className="p-col-12">
          <Panel
            header={
              <div className="update-translations-panel-header">
                <FormattedMessage
                  id="menu.item-bank-management.add-itembank-content"
                  defaultMessage="Add ItemBank Content"
                />
              </div>
            }
          >
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmittingForm}
            >
              <Form>
                <Switch>
                  <Route
                    exact
                    path={`${path}`}
                    component={() => <AddDimension availableSections={config.availableSections} />}
                  />
                  <Route
                    exact
                    path={`${path}/add-cognitive-context`}
                    component={() => (
                      <AddContext
                        availableContextTypes={config.availableContextTypes}
                      />
                    )}
                  />
                  <Route
                    exact
                    path={`${path}/add-behavior-items`}
                    component={() => (
                      <AddBehaviorItems
                        availableItemTypes={config.availableItemTypes}
                        availableItemAnswerTypes={config.availableItemAnswerTypes}
                        availableValenceTypes={config.availableValenceTypes}
                        availableContexts={config.availableContexts}
                      />
                    )}
                  />
                  <Route
                    exact
                    path={`${path}/add-cognitive-items`}
                    component={() => (
                      <AddCognitiveItems
                        availableItemTypes={config.availableItemTypes}
                        availableItemAnswerTypes={config.availableItemAnswerTypes}
                        availableContexts={config.availableContexts}
                      />
                    )}
                  />
                  <Route
                    exact
                    path={`${path}/add-report-texts`}
                    component={() => (
                      <AddReportTexts
                        availableReportTextTypes={config.availableReportTextTypes}
                        availableTextLevels={config.availableTextLevels}
                        availableSequences={config.availableSequences}
                        reportTextValues={config.reportTextValues}
                        processFormValues={(values) => processFormValues(values)}
                      />
                    )}
                  />
                </Switch>
              </Form>
            </Formik>
          </Panel>
        </div>
      </div>
    </div>
  );
};

export default AddItemBankAssessmentContent;
